import { ALL_FEATURES, mayCarry, mayRunCellar, type Feature } from "@corklog/shared";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useCellar, useLastCellar } from "../cellar.js";
import { useSignedInUser } from "../session.js";

/**
 * The heading of a cellar's page, and the way to the other two.
 *
 * **Since 2026-10-07 the row is three entries and *Mer*.** It had grown to
 * nine and wrapped onto three lines on a phone, and the notes below record a
 * row defending its length one entry at a time. The owner asked for the
 * sections used less often to go under another, so *Viner*, *Att öppna* and
 * *Till maten* stay in the row and the rest are grouped under *Mer* — see
 * `MENU_GROUPS` and `project-scope.md`. The reasoning below about where each
 * section stands still decides its group and its neighbours; what it says
 * about the row's length is history.
 *
 * A cellar *is* its wines — opening one lands on the list of bottles, which is
 * what somebody standing in front of a cabinet came for. The cabinets and
 * shelves themselves are furniture: set up once, corrected rarely, and
 * therefore a page of their own rather than the thing in the way of the list.
 *
 * Each link is labelled with the title of the page it leads to, so the word
 * you click is the word you arrive at. The row is quiet text on the heading's
 * baseline, not tabs and not buttons: a row of sections does not need a navigation
 * bar built for them either.
 *
 * The export is last and is a page rather than a button because it has to be
 * explained before it is useful — two files answering two different questions.
 * It sits here, beside the furniture, rather than on the list of wines: it is
 * an act of ownership over the whole cellar, not something done to a wine.
 *
 * *Att öppna* comes directly after the wines, because it is the list read a
 * second way rather than a place of its own — the same rows, ordered by what
 * is running out. It is also the question this application was built for, and
 * a link to it standing after the cabinets would say the opposite.
 *
 * *Till maten* stands next, and had to become a section of its own on
 * 2026-09-18: `10.1` built it into *Att öppna* and the two lists there are
 * drawn with the same row, so a bottle recommended for tonight's dinner and a
 * bottle whose window is closing were indistinguishable on one page. It sits
 * beside *Att öppna* because it is the same evening asked about from the other
 * end — from the kitchen rather than from the cellar — and because a question
 * asked while cooking should be one tap from the front of a cellar.
 *
 * *Inventering* is reached from *Luckor* and is not in the row, since
 * 2026-09-18. It was beside it until then, on the observation that the two are
 * the same kind of errand — a walk past the cabinets with a phone — differing
 * only in what is being asked of each bottle: what the record does not know
 * about it, and whether it is still there. That is the reason it was the one
 * to leave when *Till maten* needed a place: the two are one errand, and the
 * page you are already on when you think of the other one is *Luckor*. The row
 * stays nine long, which is the point — a tenth entry was refused at `9.8` on
 * the grounds that eight already wrap onto three lines on a phone, and the
 * answer to a section worth having is to move one out rather than to stop
 * counting.
 *
 * *Orden* is not in the row at all — it is reached from *Producenter*, which
 * is the vocabulary anybody comes looking for first, and the row had already
 * refused a tenth entry once on the grounds that eight of them wrap onto three
 * lines on a phone.
 *
 * *Statistik* sits beside the export, and for the same reason: both are acts
 * of ownership over the whole cellar rather than something done to a wine, and
 * neither is reached on the way to a bottle. It comes first of the two because
 * the export is deliberately last.
 *
 * *Luckor* sits between the wines and the furniture because that is what it is
 * about: the wines, read shelf by shelf. It stays in the row when there is
 * nothing left to fill in — the screen behind it then says so in a sentence,
 * which is a better answer than a link that comes and goes depending on
 * whether a Champagne somewhere is missing a date.
 */
/**
 * *Till serveringen* is in this list and not in the row, which is the one
 * exception and is deliberate. It is reached from *Att öppna*, because the
 * prompt belongs where the question is asked — and because a section that is
 * empty most of the year should not stand in the row all year, on a phone
 * where eight of them already wrap onto three lines. It still needs a title
 * here: the page it leads to wears this heading like every other.
 */
/**
 * *Gäster* stands beside *Statistik* and the export, and for the reason those
 * two stand together: it is an act of ownership over the whole cellar rather
 * than something done to a wine. It comes before the export because the export
 * is deliberately last.
 *
 * **A guest does not see it, since `9.2`.** Until then this component was
 * handed a cellar id and a section and did not know who was reading, so the
 * link stood in the row for everybody and the page behind it answered a guest
 * in a sentence. The role now arrives from `CellarContext`, fetched once above
 * the routes rather than on every page, which is the fact that was missing.
 * The sentence on the page stays: a link can still be typed, or kept in a
 * bookmark from before somebody's access changed.
 */
/**
 * *Överlåta källaren* is the second exception, added at `9.8`, and it is the
 * same one: reached from *Gäster*, because that page is already the answer to
 * who reaches this cellar and handing it over is that question at its limit.
 * It is done once in the life of a cellar, if ever, and a row that refused a
 * tenth entry on the grounds that eight already wrap onto three lines on a
 * phone is not where it belongs. It still needs a title here, like every other
 * page that wears this heading.
 */
/**
 * *Anteckningar* is the third exception, added at `10.10`, and it is the one
 * that is reached from the wine list itself.
 *
 * The line above that list is the indicator — *två nya anteckningar* — and the
 * indicator is the way in, so a row entry would be a second door onto the same
 * page standing beside the first. It is also the entry the row could least
 * afford: nine is what `9.8` settled on when it refused a tenth, and this is a
 * page whose news arrives on the page you are already on.
 *
 * A guest is shown it, unlike the five owner-only sections. What is gathered
 * there is legible to them one wine at a time already, and a guest reading
 * what the other guests made of the same bottle is the conversation the
 * sharing is for.
 */
export type CellarSection =
  | "wines"
  | "notes"
  | "wishes"
  | "drunk"
  | "toOpen"
  | "meal"
  | "serving"
  | "gaps"
  | "stocktake"
  | "storages"
  | "producers"
  | "statistics"
  | "vocabulary"
  | "quality"
  | "guests"
  | "transfer"
  | "export"
  // The two pages outside every cellar that wear this heading since
  // 2026-10-08, so the way on from them is the same row and *Mer*.
  | "discover"
  | "areaReports"
  | "settings";

const SECTIONS: readonly {
  section: CellarSection;
  path: string;
  title: string;
  /** Kept out of the row, and reached from the page it belongs to. */
  aside?: true;
  /**
   * The owner's alone, from `9.2` — and from `Contributor`'s day, 2026-09-21,
   * that is a narrower set than *not a guest*.
   *
   * Three of these are lists of the owner's unfinished work — what the record
   * does not know, what has not been counted, what should be carried to the
   * fridge — and a reader who cannot act on any of it is being handed a
   * stranger's to-do list. That argument reaches a contributor as squarely as
   * a guest: somebody invited to drink here does not fill in disgorgement
   * dates. The rest are already 403 behind the link.
   */
  owners?: true;
  /** Shown to whoever may carry a bottle out of here: the owner and a
   *  husgäst, not a guest. One section wears it — the meal question — because
   *  asking it is the act of somebody about to open a bottle. It is also the
   *  whole of what a husgäst has over a guest in this row. */
  carriers?: true;
  /** A question to the model, drawn only while the administrator's switch
   *  for this account leaves it on. Since 2026-09-28; see `project-scope.md`. */
  feature?: Feature;
  /**
   * Under *Mer* rather than in the row, and in which of its groups — since
   * 2026-10-07. Absent for the three the row keeps, and for the pages
   * reached from another page, which stay reached from there.
   */
  group?: MenuGroup;
  /** A page in no cellar, so its path is the whole address rather than one
   *  under `/kallare/:id`, and it is offered with no cellar in hand. */
  outside?: true;
  /** Shown only where the administrator's switches show *Upptäck* to this
   *  account — `mayDiscover`, resolved by the server. */
  discover?: true;
  /** The administrator's alone. */
  administrator?: true;
}[] = [
  { section: "wines", path: "", title: "wines.title" },
  {
    section: "notes",
    path: "/anteckningar",
    title: "notes.title",
    // Reached from the line above the wine list, which is the indicator that
    // says there is something new there. See the note above.
    aside: true,
  },
  { section: "toOpen", path: "/oppna", title: "toOpen.title" },
  // Asking spends money, which is why a guest is not shown the way in. A
  // contributor is: the question is *what should I open with this*, asked by
  // somebody who is going to open it, and the role exists for exactly that
  // evening. The route moved from `write` to `carry` with them.
  {
    section: "meal",
    path: "/mat",
    title: "meal.title",
    carriers: true,
    feature: "meal",
  },
  {
    section: "serving",
    path: "/servering",
    title: "serving.title",
    aside: true,
    owners: true,
  },
  { section: "gaps", path: "/luckor", title: "gaps.title", owners: true, group: "walk" },
  {
    section: "stocktake",
    path: "/inventering",
    title: "stocktake.title",
    // Reached from *Luckor*, which is the same walk past the cabinets with a
    // different question. It left the row on 2026-09-18 so that *Till maten*
    // could take a place rather than add a tenth, and was reached only from
    // *Luckor* until 2026-10-19, when it proved too well hidden: it has its
    // own entry under *Mer*, beside the page it was reached from.
    owners: true,
    group: "walk",
  },
  // **The cabinets and shelves left the row for everybody but the owner on
  // 2026-09-21.** It is the furniture: what it offers is renaming a cabinet,
  // adding a shelf and saying how many bottles fit, and none of that is
  // anybody's to do but the owner's — so a reader was being shown a page whose
  // every control was withheld from them. Where a bottle stands is on the
  // bottle's own row in the wine list, which is where somebody reading this
  // cellar actually asks the question.
  {
    section: "storages",
    path: "/skap",
    title: "cellar.title",
    owners: true,
    group: "walk",
  },
  {
    section: "producers",
    path: "/producenter",
    title: "producers.title",
    group: "about",
  },
  {
    section: "vocabulary",
    path: "/ord",
    title: "vocabulary.title",
    // Reached from *Producenter*, like *Till serveringen* is reached from *Att
    // öppna*: the row already wraps onto three lines on a phone, and a word
    // nobody is looking for should not stand in it all year.
    aside: true,
    owners: true,
  },
  {
    section: "quality",
    path: "/kvalitet",
    title: "quality.title",
    // Under *Om källaren* beside *Producenter* since 2026-10-08, on the owner's
    // asking; reached from *Producenter* until then, and still linked from
    // there — half of what it proposes is a producer's standing. The owner's,
    // because asking spends money.
    owners: true,
    feature: "quality",
    group: "about",
  },
  {
    section: "statistics",
    path: "/statistik",
    title: "statistics.title",
    group: "about",
  },
  // What was opened last, newest first. Under *Om källaren* beside the
  // statistics, which count the same drinkings by the year; shown to everybody
  // who can see the cellar, as the wine pages' own history already is.
  {
    section: "drunk",
    path: "/drucket",
    title: "drunk.title",
    group: "about",
  },
  // The wines the reader would like to have, since 2026-10-17. Under *Om
  // källaren* with the other things read about the cellar, and shown to
  // everybody who can see it: a wish is private to whoever made it, so there is
  // nothing in it a guest could be shown that is not their own.
  {
    section: "wishes",
    path: "/onskelista",
    title: "wishes.title",
    group: "about",
  },
  {
    section: "guests",
    path: "/gaster",
    title: "guests.title",
    owners: true,
    group: "share",
  },
  {
    section: "transfer",
    path: "/overlata",
    title: "transfer.title",
    // Reached from *Gäster*, like *Orden* is reached from *Producenter*.
    aside: true,
    owners: true,
  },
  {
    section: "export",
    path: "/export",
    title: "export.title",
    owners: true,
    group: "share",
  },
  {
    section: "discover",
    path: "/upptack",
    title: "discover.title",
    outside: true,
    discover: true,
    group: "beyond",
  },
  // The administrator's runs and the wishes waiting on them, since 2026-10-08
  // an entry of its own beside *Upptäck*, which it is the back room of. Reached
  // until then from *Inställningar* and from a link on *Upptäck*.
  {
    section: "areaReports",
    path: "/installningar/omraden",
    title: "areaReports.title",
    outside: true,
    administrator: true,
    group: "beyond",
  },
  // The administrator's switches, since 2026-10-08 an entry here as well as
  // the cog in the header. Last, because they reach every cellar at once and
  // are opened least of anything in the menu.
  {
    section: "settings",
    path: "/installningar",
    title: "settings.title",
    outside: true,
    administrator: true,
    group: "beyond",
  },
];

/**
 * *Mer*'s groups, in the order they are drawn: what is done walking past the
 * cabinets, what is read about the cellar, what is handed to somebody else,
 * and what is outside this cellar altogether. Named for the errand rather
 * than for the pages, so a reader looking for *where do I see what the cellar
 * cost* finds *Om källaren* before they find *Statistik*.
 */
type MenuGroup = "walk" | "about" | "share" | "beyond";
const MENU_GROUPS: readonly MenuGroup[] = ["walk", "about", "share", "beyond"];

export function CellarHeading({
  cellarId,
  current,
}: {
  /**
   * The cellar whose page this is — or null on a page that stands in none,
   * which then draws the row and *Mer* of the cellar last opened in this tab,
   * and only the entries outside a cellar when there is none.
   */
  cellarId: string | null;
  current: CellarSection;
}) {
  const { t } = useTranslation();
  const here = SECTIONS.find((section) => section.section === current);
  const user = useSignedInUser();

  // Inside a cellar, the cellar and the reader's role come from the provider
  // above the routes. Outside every cellar there is no provider value, so the
  // last cellar is looked up here — and the provider is deliberately not
  // pointed at it, since what it holds also decides the administrator's
  // switches for every screen beneath it, and *Rapporter och önskemål* is not
  // in that cellar.
  const inCellar = useCellar();
  const last = useLastCellar(cellarId === null);
  const cellar = cellarId === null ? last.cellar : inCellar;
  const id = cellarId ?? last.id;
  const known = cellarId === null ? last.settled : inCellar !== null;

  const mayWrite = cellar === null ? false : mayRunCellar(cellar.role);
  const mayBring = cellar === null ? false : mayCarry(cellar.role);
  // Resolved against the menu's own cellar rather than read through
  // `useFeatures`, which asks the provider — empty outside a cellar.
  const features =
    user === null
      ? ALL_FEATURES
      : ((id === null ? undefined : user.cellarFeatures[id]) ?? user.features);

  // What this reader may be offered at all, by the rules the row has kept
  // since `9.2`: the role, the husgäst's one extra, the administrator's
  // switches, and — for the cellar's own sections — a cellar to be in. Then
  // split between the row and the menu.
  const offered = (known ? SECTIONS : []).filter(
    (section) =>
      section.aside !== true &&
      (section.outside === true || id !== null) &&
      (mayWrite || section.owners !== true) &&
      (mayBring || section.carriers !== true) &&
      (section.feature === undefined || features[section.feature]) &&
      (section.discover !== true || user?.mayDiscover === true) &&
      (section.administrator !== true || user?.isAdministrator === true),
  );
  const pathOf = (section: (typeof SECTIONS)[number]): string =>
    section.outside === true ? section.path : `/kallare/${id ?? ""}${section.path}`;
  const row = offered.filter(
    (section) => section.group === undefined && section.section !== current,
  );
  const groups = MENU_GROUPS.map((group) => ({
    group,
    entries: offered
      .filter((section) => section.group === group)
      .map((section) => ({
        key: section.section,
        to: pathOf(section),
        label: t(section.title),
        here: section.section === current,
      })),
  })).filter((group) => group.entries.length > 0);

  return (
    // Wrapping, and it has to: nine sections do not fit across a phone, and a
    // row that does not wrap makes the whole page wider than the screen. The
    // browser then shrinks the page to fit — which reads as a slightly small
    // page and behaves as one where every tap lands a little above where it
    // was aimed, worse the further down the page you are. Found by the
    // journeys at 8.2, having arrived unnoticed with the fifth section at 8.1.
    // Relative, so *Mer*'s panel hangs from the heading's right edge rather
    // than from the button: on a narrow phone the row wraps under the heading
    // and the button stands part-way along it, and a panel aligned to the
    // button ran off the left of the screen at 320 pixels.
    <div className="relative flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
      <h1 className="font-display text-3xl">{t(here?.title ?? "wines.title")}</h1>
      {/* Drawn only once it is known who is reading. A row that guessed would
          either flash links a guest may not follow or hide the owner's own
          links from them for a moment on every page, and the owner is the
          person who opens this application every day. Arriving from the list
          of cellars the role is already in hand, so this waits on nothing; a
          link opened cold costs one request, and the heading above stands
          while it is in flight. */}
      <nav className="flex flex-wrap items-baseline gap-x-4 gap-y-2 text-sm">
        {row.map((section) => (
          <Link
            key={section.section}
            to={pathOf(section)}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t(section.title)}
          </Link>
        ))}
        {known && <MoreMenu groups={groups} name={user?.name ?? null} />}
      </nav>
    </div>
  );
}

type MenuEntry = { key: string; to: string; label: string; here: boolean };

/**
 * *Mer*: the sections used monthly or less, in their groups, and who is
 * signed in. Since 2026-10-07; the decision is in `project-scope.md`.
 *
 * **A disclosure, not an ARIA menu.** It opens a short list of ordinary
 * links, and a link list is navigated the way every other link on the page
 * is — Tab, Enter — which the `menu` role would replace with arrow keys a
 * reader has to know to press. So it is a button with `aria-expanded` and the
 * panel it controls, closed by Escape, by a tap outside it and by following
 * one of its links.
 *
 * The page being read is listed but not linked, marked as where you are: the
 * row has never offered the page you are on, and inside a list of eight a
 * missing entry reads as a missing page rather than as *you are here*.
 */
function MoreMenu({
  groups,
  name,
}: {
  groups: readonly { group: MenuGroup; entries: readonly MenuEntry[] }[];
  name: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={wrapper}>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((shown) => !shown)}
        className="text-accent inline-flex items-baseline gap-1 underline-offset-4 hover:underline"
      >
        {t("nav.more")}
        <svg
          viewBox="0 0 10 6"
          aria-hidden="true"
          className={`h-1.5 w-2.5 self-center transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M1 1l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {/* Right-aligned under the heading's row — see the note on it — and
          never wider than the screen less its margins. */}
      <div
        id={panelId}
        hidden={!open}
        className="bg-surface border-line absolute top-full right-0 z-30 mt-2 w-64 max-w-[calc(100vw-2.5rem)] rounded-sm border p-4 shadow-lg"
      >
        {groups.map(({ group, entries }) => (
          // A caption rather than a heading: the page's own headings are the
          // outline a screen reader walks, and a menu that is closed most of
          // the time should not put four entries into it.
          <section key={group} className="mb-3 last:mb-0">
            <p id={`${panelId}-${group}`} className="text-muted text-xs">
              {t(`nav.group.${group}`)}
            </p>
            <ul className="mt-1" aria-labelledby={`${panelId}-${group}`}>
              {entries.map((entry) => (
                <li key={entry.key}>
                  {entry.here ? (
                    <span aria-current="page" className="text-ink block py-1.5">
                      {entry.label}
                    </span>
                  ) : (
                    <Link
                      to={entry.to}
                      onClick={() => setOpen(false)}
                      className="text-accent block py-1.5 underline-offset-4 hover:underline"
                    >
                      {entry.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {name !== null && (
          <p className="border-line text-muted mt-3 border-t pt-3 text-xs">
            {t("nav.signedInAs", { name })}
          </p>
        )}
      </div>
    </div>
  );
}
